/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Direct Mixpanel JS for the quiz (Weddings Prod project 2928008).
 * Conventions match Poppy-Flowers/poppy-landing-pages public/poppy-mp.js (#122/#123):
 * cookie_domain .poppyflowers.com, batch_requests false, ip disabled, GPC opt-out,
 * env super-prop, first-touch UTMs, mp_did adoption so LP -> quiz is one journey.
 */
import { getOptibaseTestedUserId } from './optibase'

export const MIXPANEL_PROJECT = 'Weddings Prod'
export const MIXPANEL_PROJECT_ID = 2928008
export const MIXPANEL_TOKEN = '289348a1c8c6e04a8d7b0f567d34f25d'
export const MIXPANEL_LIB_SRC = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js'
export const MIXPANEL_TRACK_URL = 'https://api-js.mixpanel.com/track/'
export const QUIZ_PRODUCTION_HOSTNAME = 'quiz.poppyflowers.com'
export const MP_DID_PARAM = 'mp_did'
export const MIXPANEL_PAGE_VIEWED = 'Page Viewed'
export const BEACON_DEVICE_ID_KEY = 'poppy_mp_beacon_did'

export const FIRST_TOUCH_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

export const FIRST_TOUCH_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'] as const

// Meta ad URL tags. Sent as their own properties because Mixpanel cuts $current_url at 255 characters.
export const FIRST_TOUCH_AD_KEYS = ['ad_id', 'campaign_id'] as const

export const NAVIGATION_TRACK_OPTIONS = Object.freeze({
  transport: 'sendBeacon' as const,
  send_immediately: true,
})

const PII_PARAM_KEYS = new Set([
  'email',
  'e-mail',
  'e_mail',
  'mail',
  'phone',
  'tel',
  'telephone',
  'mobile',
  'first_name',
  'firstname',
  'last_name',
  'lastname',
  'full_name',
  'fullname',
  'name',
  'address',
  'street',
  'zip',
  'zipcode',
  'postal',
  'postal_code',
  '$email',
  '$name',
  '$phone',
])

const EMAIL_VALUE = /[^\s@]+@[^\s@]+\.[^\s@]+/
// Phone-like only: leading + or separators. Do not match ISO dates (`2026-10-14`)
// or bare numeric ids (`12345678901`).
const ISO_DATE_VALUE = /^\d{4}-\d{2}-\d{2}$/
const PHONE_VALUE = /^(?:\+[1-9][\d()\-\s.]{7,}|(\(?\d{2,4}\)?[\s.\-]+\d[\d\s.\-]{5,}))$/

export type MixpanelTrafficEnv = 'production' | 'preview'

export type MixpanelClient = {
  init: (token: string, config?: Record<string, unknown>, name?: string) => void
  track: (...args: any[]) => void
  identify: (id: string) => void
  register?: (props: Record<string, unknown>) => void
  register_once?: (props: Record<string, unknown>) => void
  opt_out_tracking?: () => void
  get_distinct_id?: () => string
  people?: { set_once?: (props: Record<string, unknown>) => void }
}

let booted = false
let bootPromise: Promise<{ enabled: boolean; reason?: string }> | null = null
let stubFirstTouchQueued = false
let liveFirstTouchApplied = false
let linkedBeaconId = ''

export const mixpanelTrafficEnv = (hostname: string | null | undefined): MixpanelTrafficEnv =>
  hostname === QUIZ_PRODUCTION_HOSTNAME ? 'production' : 'preview'

export const shouldOptOutTracking = (navigatorLike?: { globalPrivacyControl?: unknown } | null): boolean =>
  Boolean(navigatorLike && navigatorLike.globalPrivacyControl)

const looksLikePiiValue = (value: unknown): boolean => {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!trimmed) return false
  if (EMAIL_VALUE.test(trimmed)) return true
  if (ISO_DATE_VALUE.test(trimmed)) return false
  return PHONE_VALUE.test(trimmed)
}

const isPiiKey = (key: string): boolean => PII_PARAM_KEYS.has(String(key).toLowerCase())

export const omitPii = (properties: Record<string, unknown> | null | undefined): Record<string, unknown> => {
  const clean: Record<string, unknown> = {}
  if (!properties || typeof properties !== 'object') return clean
  Object.entries(properties).forEach(([key, value]) => {
    if (value == null || value === '') return
    if (isPiiKey(key)) return
    if (typeof value === 'string' && looksLikePiiValue(value)) return
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      clean[key] = omitPii(value as Record<string, unknown>)
      return
    }
    clean[key] = value
  })
  return clean
}

export const extractFirstTouchFromUrl = (input: {
  href?: string
  search?: string
  referrer?: string
  optibaseUid?: string
  hostname?: string
}): Record<string, string> => {
  const params = new URLSearchParams(input.search || '')
  if (!input.search && input.href) {
    try {
      const url = new URL(input.href)
      url.searchParams.forEach((value, key) => {
        if (!params.has(key)) params.set(key, value)
      })
    } catch {
      // ignore malformed href
    }
  }

  const props: Record<string, string> = {}
  FIRST_TOUCH_UTM_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value) props[key] = value
  })
  FIRST_TOUCH_CLICK_ID_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value) props[key] = value
  })
  FIRST_TOUCH_AD_KEYS.forEach((key) => {
    const value = params.get(key)
    if (value) props[key] = value
  })

  const landingPath = params.get('lp1') || ''
  if (landingPath) {
    props.lp1 = landingPath
    props.landing_path = landingPath
  }

  if (typeof input.referrer === 'string' && input.referrer.trim()) {
    props.first_referrer = input.referrer.trim()
  }

  const optibase = typeof input.optibaseUid === 'string' ? input.optibaseUid.trim() : ''
  if (optibase) props.optibase_uid = optibase

  return omitPii(props) as Record<string, string>
}

export const readMpDid = (search?: string, href?: string): string => {
  const params = new URLSearchParams(search || '')
  const fromSearch = params.get(MP_DID_PARAM)
  if (fromSearch) return fromSearch
  if (!href) return ''
  try {
    return new URL(href).searchParams.get(MP_DID_PARAM) || ''
  } catch {
    return ''
  }
}

/**
 * Portal / Poppy customer id if the last-step or proposal payload already has it.
 * Quiz submit (`ISubmitQuizQuestionResponse`) does not currently include these fields.
 */
export const portalUserIdFromPayload = (payload: unknown): string => {
  if (!payload || typeof payload !== 'object') return ''
  const rec = payload as Record<string, any>
  const nested = rec.recordResult && typeof rec.recordResult === 'object' ? rec.recordResult : {}
  const candidates = [
    rec.poppyCustomerId,
    rec.rootPortalUserId,
    rec.customerId,
    rec.portalUserId,
    rec.customer?.id,
    rec.customerInfo?.id,
    rec.poppyCustomer?.id,
    nested.poppyCustomerId,
    nested.rootPortalUserId,
    nested.customerId,
    nested.portalUserId,
    nested.customer?.id,
    nested.customerInfo?.id,
  ]
  const found = candidates.find((value) => value != null && String(value).trim() !== '')
  return found == null ? '' : String(found)
}

export const getMixpanelClient = (): MixpanelClient | null => {
  if (typeof window === 'undefined') return null
  const mp = (window as { mixpanel?: MixpanelClient }).mixpanel
  // The official snippet stub is an Array with queued track/identify. Treat it as
  // a client so events fired before mixpanel-2-latest.min.js loads are not dropped.
  if (!mp || typeof mp.track !== 'function') return null
  return mp
}

/** True once mixpanel-2-latest.min.js has replaced the snippet's Array stub. */
export const isMixpanelLoaded = (mp: unknown): boolean =>
  Boolean(mp) && !Array.isArray(mp) && typeof (mp as MixpanelClient).track === 'function'

/** Keep in sync with the mixpanel.init literal in public/index.html (guarded by mixpanel.test.ts). */
export const mixpanelInitOptions = (gpc: boolean) => ({
  cookie_domain: '.poppyflowers.com',
  cross_subdomain_cookie: true,
  ip: false,
  persistence: 'cookie',
  track_pageview: false,
  store_google: false,
  stop_utm_persistence: true,
  opt_out_tracking_by_default: gpc,
  batch_requests: false,
})

const installMixpanelSnippet = (win: Window & { mixpanel?: any }) => {
  if (win.mixpanel && win.mixpanel.__SV) return
  const mixpanel: any = (win.mixpanel = [])
  mixpanel._i = []
  mixpanel.init = function init(token: string, config: Record<string, unknown>, name?: string) {
    const instance = name && name !== 'mixpanel' ? (mixpanel[name] = []) : mixpanel
    mixpanel._i.push([token, config, name])
    instance.people = instance.people || []
    const methods = [
      'disable',
      'time_event',
      'track',
      'track_pageview',
      'track_links',
      'track_forms',
      'register',
      'register_once',
      'alias',
      'unregister',
      'identify',
      'reset',
      'opt_in_tracking',
      'opt_out_tracking',
      'has_opted_in_tracking',
      'has_opted_out_tracking',
      'clear_opt_in_out_tracking',
      'people.set',
      'people.set_once',
      'get_distinct_id',
    ]
    methods.forEach((method) => {
      const parts = method.split('.')
      const target = parts.length === 2 ? instance.people : instance
      const key = parts[parts.length - 1]
      target[key] = function queuedMixpanelCall(...queuedArgs: unknown[]) {
        target.push([key].concat(queuedArgs))
      }
    })
  }
  mixpanel.__SV = 1.2
}

const loadMixpanelLibrary = (doc: Document, src = MIXPANEL_LIB_SRC): Promise<MixpanelClient | null> => {
  const win = (doc.defaultView || window) as Window & { mixpanel?: any }
  if (win.mixpanel && typeof win.mixpanel.get_distinct_id === 'function' && !Array.isArray(win.mixpanel)) {
    return Promise.resolve(win.mixpanel)
  }
  installMixpanelSnippet(win)
  return new Promise((resolve, reject) => {
    const existing = doc.querySelector(`script[src="${src}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(win.mixpanel || null))
      existing.addEventListener('error', () => reject(new Error('mixpanel sdk failed to load')))
      return
    }
    const script = doc.createElement('script')
    script.src = src
    script.async = true
    script.onload = () => resolve(win.mixpanel || null)
    script.onerror = () => reject(new Error('mixpanel sdk failed to load'))
    ;(doc.head || doc.documentElement).appendChild(script)
  })
}

const storageOf = (win: Window): Storage | null => {
  try {
    return win.localStorage ?? null
  } catch {
    return null
  }
}

const rememberBeaconDeviceId = (win: Window, id: string) => {
  if (!id) return
  try {
    storageOf(win)?.setItem(BEACON_DEVICE_ID_KEY, id)
  } catch {
    // storage unavailable
  }
}

const storedBeaconDeviceId = (win: Window): string => {
  try {
    return storageOf(win)?.getItem(BEACON_DEVICE_ID_KEY) || ''
  } catch {
    return ''
  }
}

const firstTouchForWindow = (win: Window): Record<string, string> =>
  extractFirstTouchFromUrl({
    href: win.location?.href,
    search: win.location?.search,
    referrer: typeof win.document?.referrer === 'string' ? win.document.referrer : '',
    optibaseUid: (() => {
      try {
        return getOptibaseTestedUserId()
      } catch {
        return undefined
      }
    })(),
  })

/**
 * Mixpanel cookie written by the JS SDK (`mp_<token>_mixpanel`). Reading it lets a
 * beacon fired before `get_distinct_id` exists reuse the SDK's device id.
 */
export const readMixpanelCookieDistinctId = (cookie: string, token = MIXPANEL_TOKEN): string => {
  if (!cookie) return ''
  const name = `mp_${token}_mixpanel`
  const parts = cookie.split(';')
  for (let i = 0; i < parts.length; i += 1) {
    const trimmed = parts[i].trim()
    const eq = trimmed.indexOf('=')
    if (eq === -1 || trimmed.slice(0, eq) !== name) continue
    try {
      const parsed = JSON.parse(decodeURIComponent(trimmed.slice(eq + 1))) as {
        distinct_id?: unknown
        $device_id?: unknown
      }
      const id = parsed?.distinct_id || parsed?.$device_id
      if (typeof id === 'string' && id.trim()) return id
    } catch {
      // ignore malformed cookie
    }
  }
  return ''
}

const sdkDistinctId = (client: MixpanelClient): string => {
  if (!isMixpanelLoaded(client) || typeof client.get_distinct_id !== 'function') return ''
  try {
    const id = client.get_distinct_id()
    return id ? String(id) : ''
  } catch {
    return ''
  }
}

/**
 * Queue register / register_once / identify on whatever `window.mixpanel` is right now.
 * Call this synchronously before any track so a slow CDN replays super props and mp_did
 * before SQ_QUIZ_LOADED and the first Page Viewed. Safe to call more than once.
 */
export const queueFirstTouchSync = (win?: Window): void => {
  const target = win ?? (typeof window !== 'undefined' ? window : undefined)
  if (!target) return
  const mp = (target as { mixpanel?: MixpanelClient }).mixpanel
  if (!mp || typeof mp.track !== 'function') return
  if (isMixpanelLoaded(mp)) {
    if (liveFirstTouchApplied) return
    applyFirstTouchAndIdentity(mp, target)
    liveFirstTouchApplied = true
    return
  }
  if (stubFirstTouchQueued) return
  applyFirstTouchAndIdentity(mp, target)
  stubFirstTouchQueued = true
}

const applyFirstTouchAndIdentity = (client: MixpanelClient, win: Window) => {
  const gpc = shouldOptOutTracking(win.navigator)
  if (gpc && typeof client.opt_out_tracking === 'function') {
    client.opt_out_tracking()
  }

  const firstTouch = firstTouchForWindow(win)
  if (typeof client.register_once === 'function') client.register_once(firstTouch)
  if (typeof client.people?.set_once === 'function') client.people.set_once(firstTouch)

  const trafficEnv = mixpanelTrafficEnv(win.location?.hostname)
  if (typeof client.register === 'function') {
    client.register({ env: trafficEnv })
  }

  const mpDid = readMpDid(win.location?.search, win.location?.href)
  if (mpDid && typeof client.identify === 'function') {
    rememberBeaconDeviceId(win, mpDid)
    const current = sdkDistinctId(client)
    if (current !== mpDid) client.identify(mpDid)
  }

  linkBeaconDeviceId(client, win)
}

/**
 * If a navigation beacon minted `poppy_mp_beacon_did` before the SDK had a cookie id,
 * identify that id once the SDK loads so the submit event and later SDK events merge.
 * mp_did and an already-matching SDK id are left alone.
 */
const linkBeaconDeviceId = (client: MixpanelClient, win: Window) => {
  if (!isMixpanelLoaded(client) || typeof client.identify !== 'function') return
  if (readMpDid(win.location?.search, win.location?.href)) return
  const stored = storedBeaconDeviceId(win)
  const sdkId = sdkDistinctId(client)
  if (!stored) {
    if (sdkId) rememberBeaconDeviceId(win, sdkId)
    return
  }
  if (stored === sdkId || linkedBeaconId === stored) return
  client.identify(stored)
  linkedBeaconId = stored
}

export const bootPoppyMixpanel = async (
  adapters: {
    window?: Window
    document?: Document
    mixpanel?: MixpanelClient
    loadSnippet?: (doc: Document, src: string) => Promise<MixpanelClient | null>
  } = {}
): Promise<{ enabled: boolean; reason?: string }> => {
  if (typeof window === 'undefined' && !adapters.window) {
    return { enabled: false, reason: 'no_window' }
  }
  if (process.env.NODE_ENV === 'test' && !adapters.mixpanel) {
    return { enabled: false, reason: 'test' }
  }
  if (booted && !adapters.mixpanel) {
    return { enabled: Boolean(getMixpanelClient()) }
  }

  const win = adapters.window ?? window
  const doc = adapters.document ?? win.document
  const gpc = shouldOptOutTracking(win.navigator)

  let mp = adapters.mixpanel
  if (!mp) {
    try {
      const loadSnippet = adapters.loadSnippet ?? loadMixpanelLibrary
      mp = (await loadSnippet(doc, MIXPANEL_LIB_SRC)) || undefined
    } catch {
      return { enabled: false, reason: 'sdk_missing' }
    }
  }
  if (!mp || typeof mp.init !== 'function') {
    return { enabled: false, reason: 'sdk_missing' }
  }

  const alreadyInited = typeof mp.get_distinct_id === 'function' && !Array.isArray(mp)
  if (!alreadyInited && typeof mp.init === 'function') {
    mp.init(MIXPANEL_TOKEN, mixpanelInitOptions(gpc))
  }
  const live = (win as { mixpanel?: MixpanelClient }).mixpanel
  const client = live && typeof live.track === 'function' ? live : mp

  applyFirstTouchAndIdentity(client, win)
  booted = true
  return { enabled: true, reason: gpc ? 'gpc_opt_out' : undefined }
}

export const ensureMixpanelBooted = (): Promise<{ enabled: boolean; reason?: string }> => {
  if (!bootPromise) bootPromise = bootPoppyMixpanel()
  return bootPromise
}

export const mixpanelTrack = (event: string, properties?: any, options?: any, callback?: (value: any) => void) => {
  queueFirstTouchSync()
  ensureMixpanelBooted()
  const mp = getMixpanelClient()
  if (!mp) return
  const props = omitPii((properties && typeof properties === 'object' ? properties : {}) as Record<string, unknown>)
  if (typeof options === 'function') {
    mp.track(event, props, options)
    return
  }
  mp.track(event, props, options, callback)
}

export const mixpanelIdentify = (id: string, _traits?: any, _options?: any, callback?: (value: any) => void) => {
  ensureMixpanelBooted()
  const mp = getMixpanelClient()
  if (mp && id) {
    mp.identify(String(id))
  }
  if (callback) callback(undefined)
}

/**
 * Identify Mixpanel with the portal / Poppy customer id when the frontend has it.
 * No-op when the id is missing (quiz last-step payload does not currently include it).
 */
export const identifyLeadIfPresent = (payload: unknown): string => {
  const id = portalUserIdFromPayload(payload)
  if (id) mixpanelIdentify(id)
  return id
}

const mixpanelInsertId = (): string => {
  if (typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

const fallbackDeviceId = (win: Window): string => {
  const existing = storedBeaconDeviceId(win)
  if (existing) return existing
  const random =
    typeof crypto !== 'undefined' && typeof (crypto as Crypto).randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  const id = `$device:${random}`
  rememberBeaconDeviceId(win, id)
  return id
}

/**
 * Beacon distinct_id, in order: URL mp_did, loaded SDK id, Mixpanel cookie, then a
 * persisted fallback. The fallback is identified onto the SDK id once the library loads
 * (`linkBeaconDeviceId`) so a submit event is not left on an orphan id.
 */
export const resolveBeaconDistinctId = (win: Window = window): string => {
  if (!win) return ''
  const mpDid = readMpDid(win.location?.search, win.location?.href)
  if (mpDid) {
    rememberBeaconDeviceId(win, mpDid)
    return mpDid
  }
  const mp = (win as { mixpanel?: MixpanelClient }).mixpanel
  if (mp) {
    const sdkId = sdkDistinctId(mp)
    if (sdkId) {
      rememberBeaconDeviceId(win, sdkId)
      return sdkId
    }
  }
  const cookieId = typeof win.document?.cookie === 'string' ? readMixpanelCookieDistinctId(win.document.cookie) : ''
  if (cookieId) {
    rememberBeaconDeviceId(win, cookieId)
    return cookieId
  }
  return fallbackDeviceId(win)
}

const readDistinctId = (): string => {
  if (typeof window === 'undefined') return ''
  return resolveBeaconDistinctId(window)
}

export const buildFallbackTrackProperties = (
  eventProperties: Record<string, unknown>,
  distinctId: string,
  win: Window = window
): Record<string, unknown> => {
  const firstTouch = win ? firstTouchForWindow(win) : {}
  const eventProps = omitPii(eventProperties)
  return {
    ...firstTouch,
    ...eventProps,
    env: mixpanelTrafficEnv(win?.location?.hostname),
    token: MIXPANEL_TOKEN,
    distinct_id: distinctId,
    time: Date.now() / 1000,
    $insert_id: mixpanelInsertId(),
  }
}

const encodeMixpanelTrackBody = (event: string, properties: Record<string, unknown>, distinctId: string): string => {
  const payload = [
    {
      event,
      properties: buildFallbackTrackProperties(properties, distinctId),
    },
  ]
  const json = JSON.stringify(payload)
  const base64 =
    typeof btoa === 'function' ? btoa(unescape(encodeURIComponent(json))) : Buffer.from(json, 'utf8').toString('base64')
  return `data=${encodeURIComponent(base64)}`
}

/**
 * Fire-and-forget Mixpanel track for events immediately followed by navigation.
 * Uses the SDK sendBeacon transport only once the real SDK has loaded. While
 * window.mixpanel is still the snippet's Array stub (slow CDN, or an ad blocker
 * that never lets it load), a stub .track() is just an in-memory push that is lost
 * on navigation, so we POST directly to api-js.mixpanel.com/track/ via sendBeacon.
 */
export const mixpanelTrackBeacon = (event: string, properties: Record<string, unknown> = {}): boolean => {
  try {
    if (shouldOptOutTracking(typeof navigator !== 'undefined' ? navigator : undefined)) return false
    queueFirstTouchSync()
    const props = omitPii(properties)
    const mp = getMixpanelClient()
    if (mp && isMixpanelLoaded(mp)) {
      mp.track(event, props, NAVIGATION_TRACK_OPTIONS)
      return true
    }
    if (typeof navigator === 'undefined' || !navigator.sendBeacon) return false
    const distinctId = readDistinctId()
    if (!distinctId) return false
    const body = encodeMixpanelTrackBody(event, props, distinctId)
    return navigator.sendBeacon(`${MIXPANEL_TRACK_URL}?ip=0`, new Blob([body], { type: 'text/plain' }))
  } catch (error) {
    console.log('[mixpanelTrackBeacon]', error)
    return false
  }
}

export const resetMixpanelBootForTests = () => {
  booted = false
  bootPromise = null
  stubFirstTouchQueued = false
  liveFirstTouchApplied = false
  linkedBeaconId = ''
}
